import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Gift, Share2, Users, Trophy, Copy, Check, Loader2 } from "lucide-react";
import { Link } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

type ReferralCode = { code: string; shareUrl: string };

type Reward = {
  id: string;
  type: string;
  discountPercent: number;
  label: string;
  status: string;
  createdAt: string | null;
};

type ReferralStats = {
  totalReferred: number;
  tier: string;
  tierDiscount: number;
  nextTier: string | null;
  nextTierDiscount: number | null;
  nextTierReferralsAway: number | null;
  foundingChampionCap: number;
  foundingChampionSpotsRemaining: number;
};

export default function AccountRewards() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const { data: referralCode } = useQuery<ReferralCode>({
    queryKey: ["/api/my/referral-code"],
  });

  const { data: rewards = [] } = useQuery<Reward[]>({
    queryKey: ["/api/my/rewards"],
  });

  const { data: stats } = useQuery<ReferralStats>({
    queryKey: ["/api/my/referral-stats"],
  });

  // Fall back to the code already on the auth user so the share/copy buttons
  // work the instant the page mounts — the /api/my/referral-code round-trip
  // can take long enough that the user's tap arrives before it resolves, and
  // navigator.share() requires a live user gesture (no awaiting a fetch
  // first or the activation has expired by the time the share sheet tries
  // to open).
  const fallbackCode = (user as any)?.referralCode as string | undefined;
  const code = referralCode?.code ?? fallbackCode ?? null;
  const shareUrl =
    referralCode?.shareUrl ??
    (fallbackCode ? `${window.location.origin}/install?ref=${fallbackCode}` : null);

  const handleShare = async () => {
    if (!shareUrl) {
      toast({ title: "Still loading — try again in a sec" });
      return;
    }
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Stoop",
          text: "Join me on Stoop!",
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast({ title: "Link copied" });
      }
    } catch {
      /* cancelled */
    }
  };

  const handleCopyCode = async () => {
    if (!shareUrl) {
      toast({ title: "Still loading — try again in a sec" });
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <div className="container mx-auto max-w-2xl px-4 py-6 space-y-4">
          <div>
            <Link href="/account">
              <Button variant="ghost" size="sm" className="gap-1">
                ← Back to account
              </Button>
            </Link>
            <h1 className="mt-2 text-2xl font-bold flex items-center gap-2">
              <Gift className="h-6 w-6 text-primary" />
              Rewards & Referrals
            </h1>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Your referral code</CardDescription>
              <CardTitle className="text-3xl font-mono tracking-wider">
                {code ?? <Loader2 className="h-6 w-6 animate-spin" />}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex gap-2">
              <Button onClick={handleShare} className="flex-1">
                <Share2 className="mr-2 h-4 w-4" />
                Share invite link
              </Button>
              <Button variant="outline" onClick={handleCopyCode}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </CardContent>
          </Card>

          {stats && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4 text-primary" />
                  Your referrals
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border bg-muted/30 p-3">
                    <div className="text-xs text-muted-foreground">Total referred</div>
                    <div className="text-2xl font-semibold">{stats.totalReferred}</div>
                  </div>
                  <div className="rounded-lg border bg-muted/30 p-3">
                    <div className="text-xs text-muted-foreground">Your tier</div>
                    <div className="text-lg font-semibold capitalize">{stats.tier}</div>
                    {stats.tierDiscount > 0 && (
                      <div className="text-xs text-primary">{stats.tierDiscount}% off referrals</div>
                    )}
                  </div>
                </div>
                {stats.nextTier && stats.nextTierReferralsAway != null && stats.nextTierReferralsAway > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {stats.nextTierReferralsAway} more referral
                    {stats.nextTierReferralsAway === 1 ? "" : "s"} to reach <strong>{stats.nextTier}</strong>
                    {stats.nextTierDiscount != null && ` (${stats.nextTierDiscount}% off)`}.
                  </p>
                )}
                {stats.foundingChampionSpotsRemaining > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                    <Trophy className="mr-1 inline h-3 w-3" />
                    Founding Champion: {stats.foundingChampionSpotsRemaining} of {stats.foundingChampionCap} spots left
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Available rewards</CardTitle>
              <CardDescription>
                Apply one at checkout. Earn more by referring friends.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {rewards.length === 0 ? (
                <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                  No rewards yet. Share your code to earn discounts when friends sign up.
                </div>
              ) : (
                <div className="space-y-2">
                  {rewards.map((reward) => (
                    <div
                      key={reward.id}
                      className="flex items-center justify-between gap-3 rounded-md border p-3"
                    >
                      <div className="min-w-0">
                        <div className="font-medium">{reward.discountPercent}% off</div>
                        <div className="truncate text-xs text-muted-foreground">{reward.label}</div>
                      </div>
                      <Link href="/?browse=boxes">
                        <Button size="sm" variant="outline">
                          Shop
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
